import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiService } from '../../services/api/api.service';

@Injectable()
export class LocationService {
    private readonly codaltAddress: AddressSearchResult = {
        name: 'Leerparkpromenade 40, 3312 KW Dordrecht, Nederland',
        lat: 0,
        lng: 0,
        id: 'codalt-default-suggestion',
        street: 'Leerparkpromenade',
        housenumber: '40',
        place: 'Dordrecht',
    };

    constructor(private apiService: ApiService) {}

    getLocations(
        lat: string | number,
        lng: string | number,
        source: string = 'unknown'
    ): Observable<GoogleGeocodingResult[]> {
        return this.apiService
            .getCallData$<GoogleGeocodingResponse>('geocode/reverse-geocode', { lat, lng, source })
            .pipe(map(res => res.results));
    }

    getAddresses(term: string, source: string = 'unknown'): Observable<GoogleGeocodingResult[]> {
        const address = `${term} Nederland`;
        return this.apiService
            .getCallData$<GoogleGeocodingResponse>('geocode/geocode', { address, source })
            .pipe(map(res => res.results));
    }

    searchAddresses(
        term$: Observable<string | null | undefined>,
        source: string,
        minLength: number = 3,
        debounceMs: number = 300
    ): Observable<AddressSearchResult[]> {
        let lastExecutedSearch = '';
        let lastExecutedAt = 0;
        let lastResult: AddressSearchResult[] = this.getDefaultSuggestions();

        return term$.pipe(
            map(value => (typeof value === 'string' ? value : '').trim().replace(/\s+/g, ' ')),
            debounceTime(debounceMs),
            distinctUntilChanged(),
            switchMap(search => {
                if (search.length < minLength) {
                    lastExecutedSearch = '';
                    lastExecutedAt = 0;
                    lastResult = this.getDefaultSuggestions();
                    return of(lastResult);
                }

                if (!this.shouldExecuteSearch(search, lastExecutedSearch, lastExecutedAt)) {
                    return of(lastResult);
                }

                lastExecutedSearch = search;
                lastExecutedAt = Date.now();

                return this.getAddresses(search, source).pipe(
                    map((addresses: GoogleGeocodingResult[]) =>
                        this.addNonProductionSuggestions(
                            addresses.map(address => {
                                const components: Record<string, string> = {};

                                address.address_components?.forEach((component: AddressComponent) =>
                                    component.types.forEach((type: string) => (components[type] = component.long_name))
                                );

                                return {
                                    lat: address.geometry?.location?.lat,
                                    lng: address.geometry?.location?.lng,
                                    name: address.formatted_address || components['locality'] || '',
                                    id: address.place_id || address.formatted_address || '',
                                    street: components['route'],
                                    housenumber: components['street_number'],
                                    place: components['locality'] || components['postal_town'],
                                };
                            })
                        )
                    ),
                    tap(result => (lastResult = result)),
                    catchError(() => {
                        lastExecutedSearch = '';
                        lastExecutedAt = 0;
                        return of(lastResult);
                    })
                );
            })
        );
    }

    getDefaultSuggestions(): AddressSearchResult[] {
        return this.addNonProductionSuggestions([]);
    }

    /**
     * Determines whether a new backend request should be executed.
     *
     * Decisions:
     * - If there is no previous search -> execute
     * - If the search term is exactly the same -> skip
     * - If the term changed to something unrelated -> execute immediately
     * - If an incremental change differs by 2 or more characters -> execute
     * - Otherwise (small incremental change forward or backward) -> execute only if last call was > 2 seconds ago
     */
    private shouldExecuteSearch(search: string, last: string, lastAt: number): boolean {
        if (!last) {
            return true;
        }

        if (search === last) {
            return false;
        }

        const isRelatedChange = search.startsWith(last) || last.startsWith(search);
        const hasSignificantLengthChange = Math.abs(search.length - last.length) >= 2;

        return !isRelatedChange || hasSignificantLengthChange || Date.now() - lastAt > 2000;
    }

    private addNonProductionSuggestions(addresses: AddressSearchResult[]): AddressSearchResult[] {
        if (environment.production) {
            return addresses;
        }

        if (addresses.some(address => address.id === this.codaltAddress.id)) {
            return addresses;
        }

        return [this.codaltAddress, ...addresses];
    }
}

export interface Address {
    street: string;
    housenumber: string;
    zipcode: string;
    town: string;
}

interface GoogleGeocodingResponse {
    results: GoogleGeocodingResult[];
    status: string;
}

interface GoogleGeocodingResult {
    address_components: AddressComponent[];
    formatted_address: string;
    geometry: {
        location: {
            lat: number;
            lng: number;
        };
        location_type: string;
        viewport: {
            northeast: LatLng;
            southwest: LatLng;
        };
    };
    place_id: string;
    types: string[];
}

export interface AddressSearchResult {
    lat: number;
    lng: number;
    name: string;
    id: string;
    street: string;
    housenumber: string;
    place: string;
}

export interface AddressComponent {
    long_name: string;
    short_name: string;
    types: string[];
}

export interface LatLng {
    lat: number;
    lng: number;
}
